import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { IUserJson } from '@scale/shared/types/user';
import { CountryCode } from '@scale/shared/types/demographics';
import { useParams, useHistory } from 'react-router-dom';
import {
  useGreenhouseJobPostQuery,
  createGreenhouseJobPost,
  updateGreenhouseJobPost,
} from 'react-scale/corp/lib/api/greenhouse';
import { useAllActiveSignupFlowsQuery } from 'react-scale/corp/lib/api/growth';
import {
  Button,
  BaseIcon,
  Typography,
  TextField,
  Autocomplete,
  Switch,
  SelectMenu,
  Tooltip,
  Spinner,
  Icon,
  TextArea,
} from '@scale/scaleui';
import Link from '@scale/frontend-lib/link';
import { Routes } from 'react-scale/components/GrowthCenter/urls';
import JobPostDetails from 'react-scale/components/Expert/Jobs/JobPostDetails';
import SignupFlowCountrySelect from 'react-scale/components/GrowthCenter/OnboardingControlPanel/components/signupFlow/SignupFlowCountrySelect';
import { RichTextEditor, Alert, RadioGroup, Radio } from 'design-system';
import countries, {
  countryNameFromCode,
  countryCodeFromCountryName,
} from '@scale/shared/types/countries';
import {
  PodGroup,
  JobPostQuestion,
  JOB_POST_DOMAINS,
  JOB_POST_LANGUAGE_CODES,
} from '@scale/shared-types';
import SAlert from 'react-s-alert';
import { HTTPError } from 'react-scale/lib/api-fetch';
import ResumeScreeningConfigAutocomplete from 'react-scale/components/GrowthCenter/OnboardingControlPanel/components/ResumeScreeningConfigAutocomplete';
import { SignupFlowInfo } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/api';
import { DuplicateJobPostModal } from 'react-scale/components/GrowthCenter/JobPost/DuplicateJobPostModal';

const extractCountryCodesFromString = (location: string): CountryCode[] => {
  // Sort country names by length in descending order to match longer names first
  const countryNames = countries.map(c => c.name).sort((a, b) => b.length - a.length);
  const regex = new RegExp(countryNames.join('|'), 'g');
  const matchedCountries = location.match(regex) || [];
  return matchedCountries
    .map(name => countryCodeFromCountryName(name))
    .filter((code): code is CountryCode => code !== null);
};

const formatLocationName = (locationCodes: string[]): string => {
  return `Remote - ${
    locationCodes.length > 0
      ? locationCodes.map(countryCode => countryNameFromCode(countryCode)).join(', ')
      : 'Global'
  }`;
};

// eslint-disable-next-line complexity
const JobPostSetupPage: React.FC<{ user: IUserJson }> = ({ user }) => {
  const { jobPostId } = useParams<{ jobPostId: string }>();

  const { data: signupFlowData } = useAllActiveSignupFlowsQuery();
  const signupFlowIdToInfo = useMemo(() => {
    return (signupFlowData ?? []).reduce((acc, signupFlow) => {
      acc[signupFlow._id] = signupFlow;
      return acc;
    }, {} as Record<string, SignupFlowInfo>);
  }, [signupFlowData]);

  const jobPostQuery = useGreenhouseJobPostQuery(jobPostId);

  const [jobName, setJobName] = useState<string>('');
  const [podGroup, setPodGroup] = useState<PodGroup | undefined>(undefined);
  const [domain, setDomain] = useState<string>('');
  const [languageCode, setLanguageCode] = useState<string>('');
  const [signupFlowId, setSignupFlowId] = useState<string>('');
  const [resumeScreeningConfig, setResumeScreeningConfig] = useState<{ id: string; title: string }>(
    { id: '', title: '' },
  );
  const [isActive, setIsActive] = useState<boolean>(false);
  const [showOnJobBoard, setShowOnJobBoard] = useState<boolean>(false);
  const [externalJobPostName, setExternalJobPostName] = useState<string>('');
  const [locationCodes, setLocationCodes] = useState<string[]>([]);
  const [description, setDescription] = useState<string>('');
  const [indentationWarningSeverity, setIndentationWarningSeverity] = useState('info');
  const [duplicateJobPostModalOpen, setDuplicateJobPostModalOpen] = useState(false);

  const [questions, setQuestions] = useState<JobPostQuestion[]>([]);
  const [requiresAllQuestionsPassed, setRequiresAllQuestionsPassed] = useState<boolean>(true);
  const [applicationFields, setApplicationFields] = useState<Record<string, string>>({});

  const onboardingFlowId = signupFlowIdToInfo[signupFlowId]?.onboardingFlow;

  const validateJobPostMessage = !signupFlowData
    ? 'Loading...'
    : !jobName ||
      !podGroup ||
      !domain ||
      !languageCode ||
      !externalJobPostName ||
      !description ||
      !signupFlowId
    ? 'Missing fields for job post'
    : !isActive && showOnJobBoard
    ? 'Job post cannot be shown on job board if it is not active'
    : questions.map(q => q.text).some(text => !text)
    ? 'Please fill in all questions'
    : questions.length > 0 && new Set(questions.map(q => q.text.trim())).size !== questions.length
    ? 'Duplicate questions are not allowed'
    : '';

  const handleCountryCheck = useCallback((countryCodes: string[]) => {
    setLocationCodes(prevLocationCodes => {
      const countryCodesSet = new Set(prevLocationCodes);
      let hasChanged = false;
      countryCodes.forEach(countryCode => {
        if (countryCodesSet.has(countryCode)) {
          countryCodesSet.delete(countryCode);
          hasChanged = true;
        } else {
          countryCodesSet.add(countryCode);
          hasChanged = true;
        }
      });
      if (hasChanged) {
        return Array.from(countryCodesSet);
      }
      return prevLocationCodes;
    });
  }, []);

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    // ql-indent is a class that is added by the rich text editor when a <li> or <ul> is nested
    // but it wont display correctly
    if (value.toLowerCase().includes('ql-indent')) {
      setIndentationWarningSeverity('warning');
    } else {
      setIndentationWarningSeverity('info');
    }
  };

  const today = new Date();
  const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
    2,
    '0',
  )}-${String(today.getDate()).padStart(2, '0')}`;
  const uploadImgBase = `upload/job-post/${formattedDate}/${user._id}`;

  useEffect(() => {
    if (jobPostQuery.data && signupFlowData) {
      setJobName(jobPostQuery.data.job_name);
      setPodGroup(jobPostQuery.data.podGroup);
      setDomain(jobPostQuery.data.domain ?? '');
      setLanguageCode(jobPostQuery.data.languageCode ?? '');
      setSignupFlowId(jobPostQuery.data.signupFlowId ?? '');
      setResumeScreeningConfig(prev => ({
        id: jobPostQuery.data.resumeScreeningConfigId,
        title: prev.title,
      }));
      setExternalJobPostName(jobPostQuery.data.title);
      setIsActive(jobPostQuery.data.isActive || jobPostQuery.data.live); // A job shown on job board (live) must be active
      setShowOnJobBoard(jobPostQuery.data.live);
      setDescription(jobPostQuery.data.content ?? '');
      setQuestions(jobPostQuery.data.questions ?? []);
      setRequiresAllQuestionsPassed(jobPostQuery.data.requiresAllQuestionsPassed ?? true);

      const countryCodes = extractCountryCodesFromString(jobPostQuery.data.location.name);

      setLocationCodes(countryCodes.map(code => CountryCode[code]));
    }
  }, [jobPostQuery?.data, signupFlowData]);

  const queryClient = useQueryClient();
  const history = useHistory();

  const createJob = async () => {
    const createdJobPost = await createGreenhouseJobPost({
      jobName,
      jobPostName: externalJobPostName,
      podGroup: podGroup!,
      domain,
      languageCode,
      signupFlowId,
      resumeScreeningConfigId: resumeScreeningConfig.id || undefined,
      location: formatLocationName(locationCodes),
      showOnJobBoard,
      isActive,
      description,
      questions,
      requiresAllQuestionsPassed: questions?.length ? requiresAllQuestionsPassed : undefined,
    });
    return createdJobPost;
  };

  const createMutation = useMutation(createJob, {
    onSuccess: createdJobPost => {
      SAlert.success(`${externalJobPostName} created successfully!`, {
        timeout: 10000,
      });
      queryClient.invalidateQueries('job-board-fetch-jobs');
      queryClient.invalidateQueries('job-post-list');
      queryClient.invalidateQueries('greenhouseJobPosts');
      queryClient.invalidateQueries(['all-jobs']);
      history.push(`${Routes.JobPostSetup}/${createdJobPost.greenhouseJobPostId}`);
    },
    onError: error => {
      if (error instanceof HTTPError) {
        SAlert.error(`Failed to create job: ${error.response?.message}`, {
          timeout: 30000,
          html: true,
        });
      } else {
        SAlert.error(`Failed to create job: ${error}`, {
          timeout: 30000,
        });
      }
    },
  });

  const updateJob = async () => {
    const updatedJobPost = await updateGreenhouseJobPost({
      jobId: jobPostQuery.data!.job_id,
      jobPostId: jobPostQuery.data!.id,
      jobName,
      jobPostName: externalJobPostName,
      podGroup: podGroup!,
      domain,
      languageCode,
      signupFlowId,
      resumeScreeningConfigId: resumeScreeningConfig.id || undefined,
      location: formatLocationName(locationCodes),
      showOnJobBoard,
      isActive,
      description,
      questions,
      requiresAllQuestionsPassed: questions?.length ? requiresAllQuestionsPassed : undefined,
    });
    return updatedJobPost;
  };

  const updateMutation = useMutation(updateJob, {
    onSuccess: updatedJobPost => {
      SAlert.success(`${externalJobPostName} updated successfully!`, {
        timeout: 10000,
      });
      queryClient.invalidateQueries([`greenhouse-job-post`, jobPostId]);
      queryClient.invalidateQueries([`job-post-description`, jobPostId]);
      queryClient.invalidateQueries('job-board-fetch-jobs');
      queryClient.invalidateQueries('job-post-list');
      queryClient.invalidateQueries('greenhouseJobPosts');
      queryClient.invalidateQueries(['all-jobs']);
      history.push(`${Routes.JobPostSetup}/${updatedJobPost.greenhouseJobPostId}`);
    },
    onError: error => {
      if (error instanceof HTTPError) {
        SAlert.error(`Failed to update job: ${error.response?.message}`, {
          timeout: 30000,
          html: true,
        });
      } else {
        SAlert.error(`Failed to update job: ${error}`, {
          timeout: 30000,
        });
      }
    },
  });

  return (
    <div className="scaleui scale-tailwind flex flex-col h-full w-full">
      <div className="flex flex-col border-b border-b-neutral-200 border-b-2 justify-between p-1 bg-neutral-0">
        <Button className="w-fit bg-transparent shadow-none" variant="neutral">
          <Link href={`${Routes.JobPost}`} as={`/corp${Routes.JobPost}`}>
            <a className="flex flex-row items-center" rel="noopener noreferrer">
              <BaseIcon contrastText edge="start" icon={['fas', 'arrow-left']} variant="neutral" />
              Exit
            </a>
          </Link>
        </Button>
        <div className="flex flex-row justify-between">
          <div className="flex flex-col">
            <Typography.Paragraph1 className="text-neutral-900 ml-2 font-medium">
              {jobPostId ? 'Update Job Post' : 'Create Job Post'}
            </Typography.Paragraph1>
            {isActive && jobPostId ? (
              <Link href={`https://app.outlier.ai/en/expert/opportunities/${jobPostId}`}>
                <a
                  className="flex flex-row items-center underline px-2 py-1 space-x-2"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <span className="font-normal text-neutral-800">{`https://app.outlier.ai/en/expert/opportunities/${jobPostId}`}</span>
                  <BaseIcon
                    contrastText
                    edge="start"
                    icon={['fas', 'external-link-alt']}
                    variant="neutral"
                  />
                </a>
              </Link>
            ) : (
              <Typography.Paragraph2 className="text-neutral-800 px-2 py-1">
                Dynamic preview of job posts. Links may not work in preview - please set the job to
                active and save to see the full experience.
              </Typography.Paragraph2>
            )}
          </div>
          <div className="flex flex-row justify-end items-end">
            {!!jobPostId && (
              <Tooltip
                side="left"
                target={
                  <Button
                    className="mr-2 mb-2"
                    size="lg"
                    variant="secondary"
                    onClick={() => setDuplicateJobPostModalOpen(true)}
                    disabled={!!validateJobPostMessage}
                  >
                    Duplicate
                  </Button>
                }
                text={validateJobPostMessage}
              />
            )}
            <DuplicateJobPostModal
              isOpen={duplicateJobPostModalOpen}
              onClose={() => setDuplicateJobPostModalOpen(false)}
              jobName={jobName}
              jobPostId={jobPostId}
            />
            <Tooltip
              side="left"
              target={
                <Button
                  className="mr-2 mb-2"
                  size="lg"
                  variant="primary"
                  onClick={() => {
                    if (jobPostId) {
                      updateMutation.mutate();
                    } else {
                      createMutation.mutate();
                    }
                  }}
                  disabled={
                    !!validateJobPostMessage || createMutation.isLoading || updateMutation.isLoading
                  }
                >
                  {jobPostId
                    ? updateMutation.isLoading
                      ? 'Updating...'
                      : 'Update Job Post'
                    : createMutation.isLoading
                    ? 'Creating...'
                    : 'Create Job Post'}
                </Button>
              }
              text={validateJobPostMessage}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-row overflow-y-auto h-full">
        {!signupFlowData || (jobPostId && jobPostQuery.isLoading) ? (
          <Spinner
            className="fixed inset-0 flex items-center justify-center"
            size="lg"
            variant="primary"
          />
        ) : (
          <>
            <div className="flex flex-col w-[40%] h-full pl-6 pr-16 py-6 gap-y-4">
              <div className="w-full">
                <Typography.Subtitle3 className="py-1">Job Post Name*</Typography.Subtitle3>
                <TextField
                  onChange={e => setExternalJobPostName(e.target.value)}
                  value={externalJobPostName}
                />
              </div>
              <div className="w-full">
                <Typography.Subtitle3 className="py-1">Internal Job Name*</Typography.Subtitle3>
                <TextField onChange={e => setJobName(e.target.value)} value={jobName} />
              </div>
              <div className="w-full">
                <Typography.Subtitle3 className="py-1">Pod*</Typography.Subtitle3>
                <SelectMenu
                  hasAdaptiveWidth={true}
                  menuDirection="bottom"
                  options={Object.values(PodGroup)
                    .filter(pod => pod !== PodGroup.None)
                    .map(pod => ({
                      id: pod,
                      name: pod.charAt(0).toUpperCase() + pod.slice(1),
                    }))}
                  // @ts-ignore undefined selectedOptionId is valid when no option is selected
                  selectedOptionId={podGroup}
                  setSelected={val => setPodGroup(val as PodGroup)}
                />
              </div>
              <div className="w-full">
                <Typography.Subtitle3 className="py-1">Domain*</Typography.Subtitle3>
                <Autocomplete
                  canCreateOption={false}
                  className="w-full"
                  options={JOB_POST_DOMAINS}
                  value={domain}
                  setSelectedOption={val => setDomain(val)}
                />
              </div>
              <div className="w-full">
                <Typography.Subtitle3 className="py-1">Language Code*</Typography.Subtitle3>
                <Autocomplete
                  canCreateOption={false}
                  className="w-full"
                  options={JOB_POST_LANGUAGE_CODES}
                  value={languageCode}
                  setSelectedOption={val => setLanguageCode(val)}
                />
              </div>
              <div className="w-full">
                <div className="flex items-center">
                  <Typography.Subtitle3 className="py-1">Signup Flow*</Typography.Subtitle3>
                  {onboardingFlowId && (
                    <Link
                      href={`${Routes.OnboardingControlPanel}/${onboardingFlowId}`}
                      as={`/corp${Routes.OnboardingControlPanel}/${onboardingFlowId}`}
                    >
                      <a className="ml-2" rel="noopener noreferrer" target="_blank">
                        <BaseIcon
                          edge="start"
                          icon={['fas', 'external-link-alt']}
                          variant="neutral"
                        />
                      </a>
                    </Link>
                  )}
                </div>
                <Autocomplete
                  canCreateOption={false}
                  className="w-full"
                  options={signupFlowData.map(flow => flow.name)}
                  value={signupFlowIdToInfo[signupFlowId]?.name ?? ''}
                  setSelectedOption={val =>
                    setSignupFlowId(signupFlowData.find(flow => flow.name === val)?._id ?? '')
                  }
                />
              </div>

              <div className="w-full">
                <div className="flex flex-col">
                  <Typography.Subtitle3>Resume Screen</Typography.Subtitle3>
                  <Typography.Caption>
                    Applied when candidates reach the Resume Screen node in OCP. If not selected,
                    the node's default screening is used. If neither is set, candidates
                    automatically pass.
                  </Typography.Caption>
                </div>
                <ResumeScreeningConfigAutocomplete
                  selectedResumeScreeningConfig={resumeScreeningConfig}
                  setSelectedResumeScreeningConfig={setResumeScreeningConfig}
                  disabled={false}
                  className={'h-3/5'}
                  buttonClassName={'h-2/3'}
                />
              </div>
              <div className="w-full">
                <Typography.Subtitle3 className="pt-1">Active*</Typography.Subtitle3>
                <Typography.Caption className="pb-1">
                  If enabled, users with the direct link will be able to apply, and job board
                  integrations will be able to see the job. If disabled, no one has access.
                </Typography.Caption>
                <Switch
                  checked={isActive}
                  onCheckedChange={newActive => {
                    setIsActive(newActive);
                    if (!newActive) {
                      setShowOnJobBoard(false);
                    }
                  }}
                />
              </div>
              <div className="w-full">
                <Typography.Subtitle3 className="pt-1">
                  Show on Public Job Board*
                </Typography.Subtitle3>
                <Typography.Caption className="pb-1">
                  If enabled, job will be shown on{' '}
                  <a
                    className="font-normal underline"
                    href="https://app.outlier.ai/en/expert/opportunities"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    app.outlier.ai/en/expert/opportunities
                  </a>
                </Typography.Caption>
                <Switch
                  checked={showOnJobBoard}
                  onCheckedChange={setShowOnJobBoard}
                  disabled={!isActive}
                />
              </div>
              <div className="w-full">
                <Typography.Subtitle3 className="pt-1">Location</Typography.Subtitle3>
                <Typography.Caption className="pb-1">
                  If none selected, job is global. This selection is only for display purposes. The
                  attached signup flow determines which countries are eligible.
                </Typography.Caption>
                <SignupFlowCountrySelect
                  className="col-span-3 self-start"
                  isCountryChecked={(countryCode: string) => locationCodes.includes(countryCode)}
                  handleCountryCheck={handleCountryCheck}
                  isDisabled={() => false}
                  checkboxLabelClassName={'text-sm'}
                />
              </div>
              {!!signupFlowId && (
                <div className="w-full">
                  <Typography.Subtitle3 className="pt-1">Questions</Typography.Subtitle3>
                  <Typography.Caption className="pb-1">
                    Users must answer these questions correctly to apply to this job
                  </Typography.Caption>
                  {questions.map((question, index) => (
                    <div key={index} className="flex flex-col">
                      <div className="mt-2 flex flex-row text-neutral-600 justify-between items-center">
                        Question {index + 1}:
                        <Icon
                          className="cursor-pointer"
                          icon={['fas', 'trash-alt']}
                          size="md"
                          onClick={() =>
                            setQuestions(prev => [
                              ...prev.slice(0, index),
                              ...prev.slice(index + 1),
                            ])
                          }
                        />
                      </div>
                      <TextArea
                        className="text-neutral-600 mb-3"
                        placeholder="Enter a Yes/No Question"
                        value={question.text}
                        onChange={e =>
                          setQuestions(prev => [
                            ...prev.slice(0, index),
                            { ...prev[index], text: e.target.value },
                            ...prev.slice(index + 1),
                          ])
                        }
                        dynamic={true}
                      />
                      <Typography.Paragraph3 className="pb-1">Correct Answer</Typography.Paragraph3>
                      <RadioGroup
                        value={question.answer ? 'yes' : 'no'}
                        onChange={value =>
                          setQuestions(prev => [
                            ...prev.slice(0, index),
                            { ...prev[index], answer: value === 'yes' },
                            ...prev.slice(index + 1),
                          ])
                        }
                      >
                        <Radio
                          radioClassName="text-sm text-neutral-600 font-normal my-2 mx-2 p-0"
                          key="yes"
                          value="yes"
                          label="Yes"
                        />
                        <Radio
                          radioClassName="text-sm text-neutral-600 font-normal my-1 mx-2 p-0"
                          key="no"
                          value="no"
                          label="No"
                        />
                      </RadioGroup>
                    </div>
                  ))}
                  <Button
                    className="mt-4"
                    variant="secondary"
                    size="lg"
                    onClick={() => setQuestions([...questions, { text: '', answer: true }])}
                  >
                    Add Question
                  </Button>
                </div>
              )}
              {questions.length > 1 && (
                <div className="w-full mt-2">
                  <Typography.Subtitle3 className="py-1">
                    Require All Questions Correct
                  </Typography.Subtitle3>
                  <RadioGroup
                    value={requiresAllQuestionsPassed ? 'allQuestions' : 'atLeastOneQuestion'}
                    onChange={value => setRequiresAllQuestionsPassed(value === 'allQuestions')}
                  >
                    <Radio
                      radioClassName="text-sm text-neutral-900 font-normal my-2 mx-2 p-0"
                      key="allQuestions"
                      value="allQuestions"
                      label="All questions"
                    />
                    <Radio
                      radioClassName="text-sm text-neutral-900 font-normal my-1 mx-2 p-0"
                      key="atLeastOneQuestion"
                      value="atLeastOneQuestion"
                      label="At least one question"
                    />
                  </RadioGroup>
                </div>
              )}
              <div className="w-full">
                <Typography.Subtitle3 className="py-1">Description*</Typography.Subtitle3>
                <RichTextEditor
                  value={description}
                  onChange={handleDescriptionChange}
                  options={{
                    uploadImgBase,
                  }}
                  enableGrammarChecking
                  ignoreBadLinks // For tracking pixels ex. <img src="https://px.ads.linkedin.com/collect/?pid=5015532&amp;conversionId=15541972&amp;fmt=gif" width="1">
                />
                <Alert className="mt-1" severity={indentationWarningSeverity as any}>
                  Please avoid using lists within lists (nested bullet points), as they may not
                  display correctly.
                </Alert>
              </div>
            </div>
            <div className="flex flex-col w-[60%] outlier-theme justify-center border-l bg-neutral-0 border-l-neutral-200 px-4 pt-8 pb-16 h-fit">
              <JobPostDetails
                job={{
                  id: jobPostQuery?.data?.id ?? 0,
                  internal_job_id: jobPostQuery?.data?.internal_job_id ?? 0,
                  location: {
                    name: formatLocationName(locationCodes),
                  },
                  title: externalJobPostName,
                  content: description,
                  departments: jobPostQuery?.data?.departments,
                  absolute_url: jobPostQuery?.data?.absolute_url,
                  questions,
                  requiresAllQuestionsPassed,
                }}
                viewOnly={true}
                applicationFields={applicationFields}
                setApplicationFields={setApplicationFields}
                signupFlowId={signupFlowId}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default JobPostSetupPage;
